import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  MenuItem,
  Stack,
  Alert,
  LinearProgress,
  useTheme,
  Chip,
  IconButton,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
  Fab,
  CircularProgress,
} from "@mui/material";
import {
  CalendarToday,
  CalendarViewWeek,
  CalendarViewMonth,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Today as TodayIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  Event as EventIcon,
} from "@mui/icons-material";

import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs"; // ✅ Import Dayjs type
import "dayjs/locale/ro";
import axios from "axios";
import { createLogger } from "./utils/logger";

// ✅ Keep date-fns for your existing date operations
import {
  format,
  formatISO,
  subDays,
  addDays,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  parseISO,
} from "date-fns";
import { ro } from "date-fns/locale";

// Enhanced Appointment Interface
interface Appointment {
  id: number;
  PatientId: number;
  DoctorId: string;
  PatientName?: string;
  DoctorName?: string;
  date: string;
  time: string;
  reason: string;
  status: "scheduled" | "completed" | "canceled" | "confirmed";
  notes?: string;
  duration?: number; // in minutes
  priority?: "low" | "medium" | "high" | "urgent";
  createdAt: string;
  updatedAt: string;
}

// Supporting Interfaces
interface Doctor {
  id: string;
  name: string;
  specialization?: string;
  email?: string;
  phone?: string;
}

interface Patient {
  id: number;
  name: string;
  email?: string;
  phone?: string;
}

interface AppointmentFilters {
  doctorId?: string;
  patientId?: number;
  status?: string;
  priority?: string;
  dateFrom?: Date;
  dateTo?: Date;
  searchTerm?: string;
}

type ViewMode = "day" | "week" | "month" | "list";

interface AppointmentScheduleProps {
  title?: string;
  height?: number;
  defaultView?: ViewMode;
}

// API Configuration
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api";
const API_ENDPOINTS = {
  appointments: "/Appointment",
  createAppointment: "/Appointment",
  updateAppointment: "/Appointment",
  deleteAppointment: "/Appointment",
  doctors: "/Appointment/doctors",
  // NOTE: Avoid loading 10k patients at startup.
  // Use paged/search endpoint for the patient picker.
  patientsPaged: "/Persoane/GetPagedPersons",
  createPatient: "/Persoane",
  // Kept as a fallback (dev/debug) if needed.
  patients: "/Appointment/patients",
};

const log = createLogger("AppointmentSchedule");

const getElapsedMs = (startedAt: number) => {
  const now =
    typeof performance !== "undefined" && typeof performance.now === "function"
      ? performance.now()
      : Date.now();
  return Math.max(0, Math.round(now - startedAt));
};

const newRequestId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

// Mock Data Generators
const generateMockDoctors = (): Doctor[] => [
  {
    id: "1",
    name: "Dr. Maria Popescu",
    specialization: "Cardiologie",
    email: "maria.popescu@clinic.ro",
    phone: "0721234567",
  },
  {
    id: "2",
    name: "Dr. Ion Ionescu",
    specialization: "Neurologie",
    email: "ion.ionescu@clinic.ro",
    phone: "0721234568",
  },
  {
    id: "3",
    name: "Dr. Ana Vasilescu",
    specialization: "Pediatrie",
    email: "ana.vasilescu@clinic.ro",
    phone: "0721234569",
  },
  {
    id: "4",
    name: "Dr. Gheorghe Marinescu",
    specialization: "Ortopedie",
    email: "gheorghe.marinescu@clinic.ro",
    phone: "0721234570",
  },
];

const generateMockPatients = (): Patient[] => [
  {
    id: 1,
    name: "Elena Gheorghiu",
    email: "elena.gheorghiu@email.ro",
    phone: "0731234567",
  },
  {
    id: 2,
    name: "Mihai Constantinescu",
    email: "mihai.constantinescu@email.ro",
    phone: "0731234568",
  },
  {
    id: 3,
    name: "Diana Popescu",
    email: "diana.popescu@email.ro",
    phone: "0731234569",
  },
  {
    id: 4,
    name: "Alexandru Radu",
    email: "alex.radu@email.ro",
    phone: "0731234570",
  },
];

const generateMockAppointments = (): Appointment[] => {
  const now = new Date();

  return [
    {
      id: 1,
      PatientId: 1,
      DoctorId: "1",
      PatientName: "Elena Gheorghiu",
      DoctorName: "Dr. Maria Popescu",
      date: format(now, "yyyy-MM-dd"),
      time: "09:00",
      reason: "Consultație cardiologie",
      status: "scheduled",
      notes: "Prima consultație",
      duration: 30,
      priority: "medium",
      createdAt: formatISO(subDays(now, 2)),
      updatedAt: formatISO(subDays(now, 1)),
    },
    {
      id: 2,
      PatientId: 2,
      DoctorId: "2",
      PatientName: "Mihai Constantinescu",
      DoctorName: "Dr. Ion Ionescu",
      date: format(addDays(now, 1), "yyyy-MM-dd"),
      time: "10:30",
      reason: "Control neurologic",
      status: "confirmed",
      notes: "Pacient cu istoric de migrene",
      duration: 45,
      priority: "high",
      createdAt: formatISO(subDays(now, 3)),
      updatedAt: formatISO(now),
    },
    {
      id: 3,
      PatientId: 3,
      DoctorId: "3",
      PatientName: "Diana Popescu",
      DoctorName: "Dr. Ana Vasilescu",
      date: format(addDays(now, 2), "yyyy-MM-dd"),
      time: "14:00",
      reason: "Vaccinare copil",
      status: "scheduled",
      duration: 15,
      priority: "low",
      createdAt: formatISO(subDays(now, 1)),
      updatedAt: formatISO(subDays(now, 1)),
    },
  ];
};

const AppointmentSchedule: React.FC<AppointmentScheduleProps> = ({
  title = "Programare Consultații",
  height = 700,
  defaultView = "week",
}) => {
  const theme = useTheme();
  dayjs.locale("ro");

  // State Management
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Patient picker state (async search)
  const [patientQuery, setPatientQuery] = useState("");
  const [patientsLoading, setPatientsLoading] = useState(false);

  // View State
  const [viewMode, setViewMode] = useState<ViewMode>(defaultView);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [, setSelectedAppointment] = useState<Appointment | null>(null);

  // Dialog State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"view" | "edit" | "add">("view");
  const [formData, setFormData] = useState<Partial<Appointment>>({});
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // New patient dialog (used from the appointment dialog)
  const [newPatientDialogOpen, setNewPatientDialogOpen] = useState(false);
  const [newPatientSaving, setNewPatientSaving] = useState(false);
  const [newPatientErrors, setNewPatientErrors] = useState<
    Record<string, string>
  >({});
  const [newPatientData, setNewPatientData] = useState<{
    NumePrenume: string;
    CNP: string;
    Email: string;
    TelefonMobil: string;
  }>({
    NumePrenume: "",
    CNP: "",
    Email: "",
    TelefonMobil: "",
  });

  // Filter State
  const [filters] = useState<AppointmentFilters>({});

  // ✅ Load Data Functions
  const loadAppointments = useCallback(
    async (filtersToApply?: AppointmentFilters) => {
      const requestId = newRequestId();
      const startedAt =
        typeof performance !== "undefined" &&
        typeof performance.now === "function"
          ? performance.now()
          : Date.now();
      try {
        setLoading(true);
        setError(null);

        const currentFilters = filtersToApply || filters;

        log.debug("Load appointments:start", {
          requestId,
          apiBaseUrl: API_BASE_URL,
          filters: currentFilters,
        });

        const params = new URLSearchParams();

        if (currentFilters.doctorId)
          params.append("doctorId", currentFilters.doctorId.toString());
        if (currentFilters.patientId)
          params.append("patientId", currentFilters.patientId.toString());
        if (currentFilters.status)
          params.append("status", currentFilters.status);
        if (currentFilters.priority)
          params.append("priority", currentFilters.priority);
        if (currentFilters.dateFrom)
          params.append("dateFrom", formatISO(currentFilters.dateFrom));
        if (currentFilters.dateTo)
          params.append("dateTo", formatISO(currentFilters.dateTo));
        if (currentFilters.searchTerm)
          params.append("search", currentFilters.searchTerm);

        const config = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "X-API-Key": process.env.REACT_APP_API_KEY,
          },
          timeout: 10000,
        };

        const url = `${API_BASE_URL}${API_ENDPOINTS.appointments}?${params.toString()}`;
        log.trace("Load appointments:request", {
          requestId,
          url,
          timeoutMs: config.timeout,
          headers: {
            hasAuthToken: Boolean(localStorage.getItem("authToken")),
            hasApiKey: Boolean(process.env.REACT_APP_API_KEY),
          },
        });

        const response = await axios.get(url, config);
        log.debug("Load appointments:response", {
          requestId,
          status: response.status,
        });

        let appointmentsData: Appointment[] = [];
        if (Array.isArray(response.data)) {
          appointmentsData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          appointmentsData = response.data.data;
        } else {
          log.warn("Load appointments:unexpected payload shape", {
            requestId,
            keys: response.data ? Object.keys(response.data) : null,
          });
        }
        log.info("Load appointments:raw data received", appointmentsData);
        const normalizedAppointments: Appointment[] = appointmentsData
          .map((a: any) => {
            const normalizeDateToYmd = (value: unknown): string => {
              if (!value) return "";

              if (value instanceof Date && !Number.isNaN(value.getTime())) {
                return format(value, "yyyy-MM-dd");
              }

              if (typeof value === "string") {
                const s = value.trim();
                if (!s) return "";

                // ISO date or ISO datetime
                if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);

                // dd.MM.yyyy or dd/MM/yyyy
                const m = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})/);
                if (m) {
                  const dd = m[1].padStart(2, "0");
                  const mm = m[2].padStart(2, "0");
                  const yyyy = m[3];
                  return `${yyyy}-${mm}-${dd}`;
                }

                const parsed = new Date(s);
                if (!Number.isNaN(parsed.getTime())) {
                  return format(parsed, "yyyy-MM-dd");
                }
              }

              return "";
            };

            const normalizeTimeToHm = (value: unknown): string => {
              if (!value) return "";
              if (typeof value === "string") {
                const s = value.trim();
                const m = s.match(/(\d{1,2}):(\d{2})/);
                if (m) return `${m[1].padStart(2, "0")}:${m[2]}`;
              }
              return "";
            };

            const patientIdRaw =
              a?.PatientId ?? a?.patientId ?? a?.patientID ?? a?.patient_id;
            const doctorIdRaw =
              a?.DoctorId ?? a?.doctorId ?? a?.doctorID ?? a?.doctor_id;

            const dateRaw =
              a?.date ??
              a?.Date ??
              a?.data ??
              a?.Data ??
              a?.appointmentDate ??
              a?.appointment_date;
            const timeRaw =
              a?.time ??
              a?.Time ??
              a?.ora ??
              a?.Ora ??
              a?.appointmentTime ??
              a?.appointment_time;

            const dateNormalized = normalizeDateToYmd(dateRaw);

            // If backend sends a single datetime string, derive time from it when time is missing.
            let timeNormalized = normalizeTimeToHm(timeRaw);
            if (
              !timeNormalized &&
              typeof dateRaw === "string" &&
              dateRaw.includes("T")
            ) {
              const tPart = dateRaw.split("T")[1];
              timeNormalized = normalizeTimeToHm(tPart);
            }

            const patientId =
              typeof patientIdRaw === "number"
                ? patientIdRaw
                : typeof patientIdRaw === "string" && patientIdRaw.trim()
                  ? Number(patientIdRaw)
                  : a?.PatientId;

            const doctorId =
              doctorIdRaw !== undefined && doctorIdRaw !== null
                ? String(doctorIdRaw)
                : a?.DoctorId !== undefined && a?.DoctorId !== null
                  ? String(a.DoctorId)
                  : "";

            const patientName =
              a?.PatientName ??
              a?.patientName ??
              a?.NumePrenume ??
              a?.numePrenume;
            const doctorName = a?.DoctorName ?? a?.doctorName;

            return {
              ...a,
              PatientId: Number.isFinite(patientId)
                ? patientId
                : (a?.PatientId as number),
              DoctorId: doctorId,
              PatientName: patientName,
              DoctorName: doctorName,
              date: dateNormalized || a?.date || "",
              time: timeNormalized || a?.time || "",
            } as Appointment;
          })
          .filter((a) => a && typeof a.id === "number");

        log.info("Load appointments:success", {
          requestId,
          count: normalizedAppointments.length,
          elapsedMs: getElapsedMs(startedAt),
        });
        log.info("Load appointments:normalized data", normalizedAppointments);
        setAppointments(normalizedAppointments);
      } catch (err) {
        const elapsedMs = getElapsedMs(startedAt);
        if (axios.isAxiosError(err)) {
          log.error("Load appointments:error", {
            requestId,
            elapsedMs,
            message: err.message,
            status: err.response?.status,
            statusText: err.response?.statusText,
          });
        } else {
          log.error("Load appointments:error (non-axios)", {
            requestId,
            elapsedMs,
            error: err,
          });
        }
        setError("Failed to load appointments");

        // Fallback to mock data in development
        if (process.env.NODE_ENV === "development") {
          log.warn("Load appointments:fallback to mock data", { requestId });
          setAppointments(generateMockAppointments());
          setError("Using mock data - API unavailable");
        }
      } finally {
        setLoading(false);
      }
    },
    [filters],
  );

  const loadDoctors = useCallback(async () => {
    const requestId = newRequestId();
    const startedAt =
      typeof performance !== "undefined" &&
      typeof performance.now === "function"
        ? performance.now()
        : Date.now();
    try {
      log.debug("Load doctors:start", {
        requestId,
        apiBaseUrl: API_BASE_URL,
      });

      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      };

      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.doctors}`,
        config,
      );
      const doctorsData = response.data.data || response.data || [];
      const normalizedDoctors: Doctor[] = (
        Array.isArray(doctorsData) ? doctorsData : []
      )
        .map((d: any) => {
          const idRaw = d?.id ?? d?.Id ?? d?.doctorId ?? d?.DoctorId;
          const name = d?.name ?? d?.Name ?? d?.DoctorName;
          if (!idRaw || !name) return null;
          return {
            id: String(idRaw),
            name: String(name),
            specialization: d?.specialization ?? d?.Specialization,
            email: d?.email ?? d?.Email,
            phone: d?.phone ?? d?.Phone,
          } as Doctor;
        })
        .filter(Boolean) as Doctor[];

      log.info("Load doctors:success", {
        requestId,
        count: normalizedDoctors.length,
        elapsedMs: getElapsedMs(startedAt),
      });
      setDoctors(normalizedDoctors);
    } catch (err) {
      log.warn("Load doctors:fallback to mock data", {
        requestId,
        elapsedMs: getElapsedMs(startedAt),
      });
      setDoctors(generateMockDoctors());
    }
  }, []);

  const loadPatients = useCallback(async (search = "", limit = 25) => {
    const requestId = newRequestId();
    const startedAt =
      typeof performance !== "undefined" &&
      typeof performance.now === "function"
        ? performance.now()
        : Date.now();
    try {
      const trimmedSearch = (search || "").trim();
      const searchLen = trimmedSearch.length;
      const searchForLogs =
        process.env.NODE_ENV === "development" ? trimmedSearch : undefined;

      const quickFilterValue: string[] = trimmedSearch
        ? trimmedSearch.split(/\s+/).filter(Boolean)
        : [];

      log.debug("Load patients:start", {
        requestId,
        apiBaseUrl: API_BASE_URL,
        searchLen,
        search: searchForLogs,
        limit,
      });

      setPatientsLoading(true);

      const config = {
        timeout: 10000,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          "X-API-Key": process.env.REACT_APP_API_KEY,
        },
      };

      // Use paged endpoint to avoid retrieving the full patients dataset.
      const requestData = {
        page: 1,
        limit,
        offset: 0,
        sortBy: "NumePrenume",
        sortDirection: "asc",
        filters: {},
        quickFilterValue,
      };

      const url = `${API_BASE_URL}${API_ENDPOINTS.patientsPaged}`;

      const response = await axios.post(url, requestData, config);

      log.debug("Load patients:response", {
        requestId,
        status: response.status,
        elapsedMs: getElapsedMs(startedAt),
        dataType: Array.isArray(response.data) ? "array" : typeof response.data,
        dataKeys:
          response.data && !Array.isArray(response.data)
            ? Object.keys(response.data)
            : undefined,
      });

      const raw = response.data?.data || response.data || [];
      //log.info("Load patients:raw data", { raw });
      const rows: any[] = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.data)
          ? raw.data
          : [];
      if (!Array.isArray(rows)) {
        log.warn("Load patients:unexpected payload shape", {
          requestId,
          elapsedMs: getElapsedMs(startedAt),
          rawType: Array.isArray(raw) ? "array" : typeof raw,
          rawKeys: raw && !Array.isArray(raw) ? Object.keys(raw) : undefined,
        });
      }

      const totalRows = rows.length;
      let invalidRows = 0;

      const mapped: Patient[] = rows
        .map((row) => {
          const idRaw =
            row?.id ?? row?.Id ?? row?.patientId ?? row?.PatientId ?? row?.ID;
          const id =
            typeof idRaw === "number"
              ? idRaw
              : typeof idRaw === "string" && idRaw.trim()
                ? Number(idRaw)
                : NaN;
          const name =
            row?.name ?? row?.numePrenume ?? row?.PatientName ?? row?.fullName;
          if (!Number.isFinite(id) || !name) {
            invalidRows += 1;
            return null;
          }
          return {
            id,
            name: String(name),
            email: row?.email ?? row?.Email,
            phone: row?.phone ?? row?.TelefonMobil,
          } as Patient;
        })
        .filter(Boolean) as Patient[];

      const uniqueById = new Map<number, Patient>();
      for (const patient of mapped) uniqueById.set(patient.id, patient);
      const patientsData = Array.from(uniqueById.values());

      log.info("Load patients:success", {
        requestId,
        elapsedMs: getElapsedMs(startedAt),
        totalRows,
        mappedRows: mapped.length,
        invalidRows,
        uniqueRows: patientsData.length,
      });

      setPatients(patientsData);
    } catch (err) {
      // If the paged endpoint is not available, don't fall back to loading all patients in production.
      // In dev, fall back to a small mock list to keep UI usable.
      const elapsedMs = getElapsedMs(startedAt);
      if (axios.isAxiosError(err)) {
        log.error("Load patients:error", {
          requestId,
          elapsedMs,
          message: err.message,
          status: err.response?.status,
          statusText: err.response?.statusText,
          responseKeys:
            err.response?.data && typeof err.response.data === "object"
              ? Object.keys(err.response.data)
              : undefined,
        });
      } else {
        log.error("Load patients:error (non-axios)", {
          requestId,
          elapsedMs,
          error: err,
        });
      }

      if (process.env.NODE_ENV === "development") {
        setPatients(generateMockPatients());
      } else {
        setPatients([]);
      }
    } finally {
      setPatientsLoading(false);
    }
  }, []);

  // ✅ Dialog Handler (defined early so CRUD callbacks can depend on it)
  const handleDialogClose = useCallback(() => {
    log.debug("Dialog:close");
    setDialogOpen(false);
    setSelectedAppointment(null);
    setFormData({});
    setFormErrors({});
    setPatientQuery("");
    setPatients([]);

    setNewPatientDialogOpen(false);
    setNewPatientSaving(false);
    setNewPatientErrors({});
    setNewPatientData({
      NumePrenume: "",
      CNP: "",
      Email: "",
      TelefonMobil: "",
    });
  }, []);

  const validateNewPatient = useCallback(() => {
    const errors: Record<string, string> = {};

    const fullName = newPatientData.NumePrenume.trim();
    const cnp = newPatientData.CNP.trim();
    const email = newPatientData.Email.trim();
    const phone = newPatientData.TelefonMobil.trim();

    if (!fullName) errors.NumePrenume = "Nume și prenume este obligatoriu";

    const cnpDigitsOnly = cnp.replace(/\s+/g, "");
    if (
      !cnpDigitsOnly ||
      cnpDigitsOnly.length !== 13 ||
      !/^\d{13}$/.test(cnpDigitsOnly)
    ) {
      errors.CNP = "CNP invalid (trebuie 13 cifre)";
    }

    if (!email) errors.Email = "Email este obligatoriu";
    else if (!/\S+@\S+\.\S+/.test(email)) errors.Email = "Email invalid";

    if (!phone) errors.TelefonMobil = "Telefon este obligatoriu";

    return errors;
  }, [newPatientData]);

  const handleCreateNewPatient = useCallback(async () => {
    const requestId = newRequestId();
    const startedAt =
      typeof performance !== "undefined" &&
      typeof performance.now === "function"
        ? performance.now()
        : Date.now();

    const errors = validateNewPatient();
    setNewPatientErrors(errors);
    if (Object.keys(errors).length > 0) {
      log.debug("New patient:validation failed", { requestId, errors });
      return;
    }

    try {
      setNewPatientSaving(true);

      const payload = {
        NumePrenume: newPatientData.NumePrenume.trim(),
        CNP: Number(newPatientData.CNP.trim()),
        Email: newPatientData.Email.trim(),
        TelefonMobil: newPatientData.TelefonMobil.trim(),
        DataNasterii: new Date(),
        Varsta: 0,
        LocalitateAdr: "",
        StradaAdr: "",
        NrAdr: "",
        BlocAdr: "",
        EtajAdr: "",
        ApartamentAdr: "",
        ScaraAdr: "",
        Observatii: "",
        isActive: true,
        rating: 1,
        diagnostice: [],
      };

      const config = {
        timeout: 10000,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          "X-API-Key": process.env.REACT_APP_API_KEY,
        },
      };

      const url = `${API_BASE_URL}${API_ENDPOINTS.createPatient}`;
      log.info("New patient:create start", {
        requestId,
        url,
        elapsedMs: getElapsedMs(startedAt),
      });

      const response = await axios.post(url, payload, config);

      const created: any = response.data;
      const createdIdRaw =
        created?.id ?? created?.Id ?? created?.patientId ?? created?.PatientId;
      const createdId =
        typeof createdIdRaw === "number"
          ? createdIdRaw
          : typeof createdIdRaw === "string" && createdIdRaw.trim()
            ? Number(createdIdRaw)
            : undefined;

      if (!createdId || !Number.isFinite(createdId)) {
        log.warn("New patient:create succeeded but missing id", {
          requestId,
          status: response.status,
          elapsedMs: getElapsedMs(startedAt),
        });
      }

      const patientToAdd: Patient = {
        id: Number.isFinite(createdId) ? (createdId as number) : Date.now(),
        name: newPatientData.NumePrenume.trim(),
        email: newPatientData.Email.trim(),
        phone: newPatientData.TelefonMobil.trim(),
      };

      setPatients((prev) => {
        const next = prev.slice();
        if (!next.some((p) => p.id === patientToAdd.id))
          next.unshift(patientToAdd);
        return next;
      });

      setFormData((prev) => ({
        ...prev,
        PatientId: patientToAdd.id,
        PatientName: patientToAdd.name,
      }));

      setPatientQuery("");
      setNewPatientDialogOpen(false);
      setNewPatientErrors({});
      setNewPatientData({
        NumePrenume: "",
        CNP: "",
        Email: "",
        TelefonMobil: "",
      });

      log.info("New patient:create success", {
        requestId,
        status: response.status,
        elapsedMs: getElapsedMs(startedAt),
        createdId: patientToAdd.id,
      });
    } catch (err) {
      const elapsedMs = getElapsedMs(startedAt);
      if (axios.isAxiosError(err)) {
        log.error("New patient:create error", {
          requestId,
          elapsedMs,
          message: err.message,
          status: err.response?.status,
          statusText: err.response?.statusText,
        });
      } else {
        log.error("New patient:create error (non-axios)", {
          requestId,
          elapsedMs,
          error: err,
        });
      }
      setNewPatientErrors((prev) => ({
        ...prev,
        _global:
          "Nu am putut crea pacientul. Verificați datele și încercați din nou.",
      }));
    } finally {
      setNewPatientSaving(false);
    }
  }, [newPatientData, validateNewPatient]);

  // ✅ CRUD Operations
  const handleSaveAppointment = useCallback(
    async (isEdit: boolean) => {
      const requestId = newRequestId();
      const startedAt =
        typeof performance !== "undefined" &&
        typeof performance.now === "function"
          ? performance.now()
          : Date.now();
      try {
        log.debug("Save appointment:start", {
          requestId,
          isEdit,
          appointmentId: formData.id,
          hasPatient: Boolean(formData.PatientId),
          hasDoctor: Boolean(formData.DoctorId),
          date: formData.date,
          time: formData.time,
        });

        setLoading(true);
        setFormErrors({});

        // Validation
        const errors: Record<string, string> = {};

        if (!formData.PatientId)
          errors.PatientId = "Pacientul este obligatoriu";
        if (!formData.DoctorId) errors.DoctorId = "Doctorul este obligatoriu";
        if (!formData.date) errors.date = "Data este obligatorie";
        if (!formData.time) errors.time = "Ora este obligatorie";
        if (!formData.reason?.trim())
          errors.reason = "Motivul este obligatoriu";

        if (Object.keys(errors).length > 0) {
          setFormErrors(errors);
          log.debug("Save appointment:validation failed", {
            requestId,
            errors,
          });
          return;
        }

        const appointmentData = {
          ...formData,
          status: formData.status || "scheduled",
          priority: formData.priority || "medium",
          duration: formData.duration || 30,
        };

        const config = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        };

        let response;

        if (isEdit && appointmentData.id) {
          log.info("Save appointment:update", {
            requestId,
            appointmentId: appointmentData.id,
          });
          response = await axios.put(
            `${API_BASE_URL}${API_ENDPOINTS.updateAppointment}/${appointmentData.id}`,
            appointmentData,
            config,
          );
        } else {
          log.info("Save appointment:create", { requestId });
          response = await axios.post(
            `${API_BASE_URL}${API_ENDPOINTS.createAppointment}`,
            appointmentData,
            config,
          );
        }

        log.info("Save appointment:success", {
          requestId,
          status: response.status,
          elapsedMs: getElapsedMs(startedAt),
        });

        await loadAppointments();
        handleDialogClose();
      } catch (err) {
        const elapsedMs = getElapsedMs(startedAt);
        if (axios.isAxiosError(err)) {
          log.error("Save appointment:error", {
            requestId,
            elapsedMs,
            message: err.message,
            status: err.response?.status,
            statusText: err.response?.statusText,
          });
        } else {
          log.error("Save appointment:error (non-axios)", {
            requestId,
            elapsedMs,
            error: err,
          });
        }

        if (axios.isAxiosError(err) && err.response) {
          if (err.response.status === 409) {
            setError("Conflict: Time slot already booked");
          } else {
            setError(`Failed to save appointment: ${err.response.statusText}`);
          }
        } else {
          setError("Failed to save appointment");
        }
      } finally {
        setLoading(false);
      }
    },
    [formData, loadAppointments, handleDialogClose],
  );

  const handleDeleteAppointment = useCallback(
    async (appointment: Appointment) => {
      if (
        !window.confirm(
          `Sigur doriți să ștergeți programarea pentru ${appointment.PatientName}?`,
        )
      ) {
        log.debug("Delete appointment:cancelled", {
          appointmentId: appointment.id,
        });
        return;
      }

      const requestId = newRequestId();
      const startedAt =
        typeof performance !== "undefined" &&
        typeof performance.now === "function"
          ? performance.now()
          : Date.now();
      try {
        log.info("Delete appointment:start", {
          requestId,
          appointmentId: appointment.id,
        });
        setLoading(true);

        const config = {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        };

        await axios.delete(
          `${API_BASE_URL}${API_ENDPOINTS.deleteAppointment}/${appointment.id}`,
          config,
        );

        log.info("Delete appointment:success", {
          requestId,
          appointmentId: appointment.id,
          elapsedMs: getElapsedMs(startedAt),
        });
        await loadAppointments();
      } catch (err) {
        const elapsedMs = getElapsedMs(startedAt);
        if (axios.isAxiosError(err)) {
          log.error("Delete appointment:error", {
            requestId,
            elapsedMs,
            message: err.message,
            status: err.response?.status,
            statusText: err.response?.statusText,
          });
        } else {
          log.error("Delete appointment:error (non-axios)", {
            requestId,
            elapsedMs,
            error: err,
          });
        }
        setError("Failed to delete appointment");
      } finally {
        setLoading(false);
      }
    },
    [loadAppointments],
  );

  // ✅ Dialog Handlers
  const handleView = useCallback((appointment: Appointment) => {
    log.debug("Dialog:view", { appointmentId: appointment.id });
    setSelectedAppointment(appointment);
    const a: any = appointment;
    const dateRaw =
      a?.date ??
      a?.Date ??
      a?.data ??
      a?.Data ??
      a?.appointmentDate ??
      a?.appointment_date;
    const timeRaw =
      a?.time ??
      a?.Time ??
      a?.ora ??
      a?.Ora ??
      a?.appointmentTime ??
      a?.appointment_time;

    const dateNormalized =
      typeof dateRaw === "string" && /^\d{4}-\d{2}-\d{2}/.test(dateRaw.trim())
        ? dateRaw.trim().slice(0, 10)
        : typeof dateRaw === "string" && dateRaw.includes("T")
          ? dateRaw.split("T")[0]
          : typeof dateRaw === "string"
            ? dateRaw.trim()
            : "";

    const timeFromRaw =
      typeof timeRaw === "string"
        ? timeRaw.match(/(\d{1,2}):(\d{2})/)?.[0]
        : "";
    const timeFromDateTime =
      typeof dateRaw === "string" && dateRaw.includes("T")
        ? dateRaw.split("T")[1]?.match(/(\d{1,2}):(\d{2})/)?.[0]
        : "";
    const timeNormalized =
      timeFromRaw || timeFromDateTime ? (timeFromRaw || timeFromDateTime)! : "";

    setFormData({
      ...appointment,
      PatientId:
        typeof a.PatientId === "number"
          ? a.PatientId
          : typeof a.patientId === "number"
            ? a.patientId
            : typeof a.PatientId === "string" && a.PatientId.trim()
              ? Number(a.PatientId)
              : typeof a.patientId === "string" && a.patientId.trim()
                ? Number(a.patientId)
                : appointment.PatientId,
      DoctorId: String(a.DoctorId ?? a.doctorId ?? ""),
      PatientName:
        a.PatientName ?? a.patientName ?? a.NumePrenume ?? a.numePrenume,
      DoctorName: a.DoctorName ?? a.doctorName,
      date: dateNormalized || appointment.date,
      time: timeNormalized || appointment.time,
    });
    setDialogMode("view");
    setDialogOpen(true);
  }, []);

  const handleEdit = useCallback((appointment: Appointment) => {
    log.debug("Dialog:edit", { appointmentId: appointment.id });
    setSelectedAppointment(appointment);
    const a: any = appointment;
    const dateRaw =
      a?.date ??
      a?.Date ??
      a?.data ??
      a?.Data ??
      a?.appointmentDate ??
      a?.appointment_date;
    const timeRaw =
      a?.time ??
      a?.Time ??
      a?.ora ??
      a?.Ora ??
      a?.appointmentTime ??
      a?.appointment_time;

    const dateNormalized =
      typeof dateRaw === "string" && /^\d{4}-\d{2}-\d{2}/.test(dateRaw.trim())
        ? dateRaw.trim().slice(0, 10)
        : typeof dateRaw === "string" && dateRaw.includes("T")
          ? dateRaw.split("T")[0]
          : typeof dateRaw === "string"
            ? dateRaw.trim()
            : "";

    const timeFromRaw =
      typeof timeRaw === "string"
        ? timeRaw.match(/(\d{1,2}):(\d{2})/)?.[0]
        : "";
    const timeFromDateTime =
      typeof dateRaw === "string" && dateRaw.includes("T")
        ? dateRaw.split("T")[1]?.match(/(\d{1,2}):(\d{2})/)?.[0]
        : "";
    const timeNormalized =
      timeFromRaw || timeFromDateTime ? (timeFromRaw || timeFromDateTime)! : "";

    setFormData({
      ...appointment,
      PatientId:
        typeof a.PatientId === "number"
          ? a.PatientId
          : typeof a.patientId === "number"
            ? a.patientId
            : typeof a.PatientId === "string" && a.PatientId.trim()
              ? Number(a.PatientId)
              : typeof a.patientId === "string" && a.patientId.trim()
                ? Number(a.patientId)
                : appointment.PatientId,
      DoctorId: String(a.DoctorId ?? a.doctorId ?? ""),
      PatientName:
        a.PatientName ?? a.patientName ?? a.NumePrenume ?? a.numePrenume,
      DoctorName: a.DoctorName ?? a.doctorName,
      date: dateNormalized || appointment.date,
      time: timeNormalized || appointment.time,
    });
    setDialogMode("edit");
    setDialogOpen(true);
  }, []);

  // ✅ Helper functions to convert between date-fns and dayjs
  const dateToString = useCallback((date: Date | string | null): string => {
    if (!date) return "";
    if (typeof date === "string") return date;
    return format(date, "yyyy-MM-dd");
  }, []);

  const handleAdd = useCallback(() => {
    log.debug("Dialog:add", {
      defaultDate: dateToString(currentDate),
    });
    setSelectedAppointment(null);
    setFormData({
      date: dateToString(currentDate), // ✅ Convert Date to string
      time: "09:00",
      status: "scheduled",
      priority: "medium",
      duration: 30,
    });
    setFormErrors({});
    setDialogMode("add");
    setDialogOpen(true);
  }, [currentDate, dateToString]);

  const stringToDayjs = useCallback((dateStr: string): Dayjs | null => {
    if (!dateStr) return null;
    return dayjs(dateStr);
  }, []);

  const stringToTime = useCallback((timeStr: string): Dayjs | null => {
    if (!timeStr) return null;
    // Create a dayjs object with today's date and the specified time
    return dayjs(`2000-01-01T${timeStr}:00`);
  }, []);

  // ✅ Enhanced Form Field Handler
  const handleFormFieldChange = useCallback(
    (field: keyof Appointment, value: any) => {
      log.trace("Form:change", { field, value });

      setFormData((prev) => {
        const newData = { ...prev };

        if (field === "date") {
          // Convert Dayjs to string
          newData[field] = dayjs.isDayjs(value)
            ? value.format("YYYY-MM-DD")
            : value;
        } else if (field === "time") {
          // Convert Dayjs to time string
          newData[field] = dayjs.isDayjs(value) ? value.format("HH:mm") : value;
        } else {
          newData[field] = value;
        }

        return newData;
      });

      if (formErrors[field]) {
        setFormErrors((prev) => ({ ...prev, [field]: "" }));
      }
    },
    [formErrors],
  );

  // ✅ Navigation Handlers
  const handleNavigate = useCallback(
    (direction: "prev" | "next") => {
      setCurrentDate((prev) => {
        const prevStr = format(prev, "yyyy-MM-dd");
        switch (viewMode) {
          case "day": {
            const next =
              direction === "next" ? addDays(prev, 1) : subDays(prev, 1);
            log.debug("Navigate", {
              direction,
              viewMode,
              from: prevStr,
              to: format(next, "yyyy-MM-dd"),
            });
            return next;
          }
          case "week": {
            const next =
              direction === "next" ? addWeeks(prev, 1) : subWeeks(prev, 1);
            log.debug("Navigate", {
              direction,
              viewMode,
              from: prevStr,
              to: format(next, "yyyy-MM-dd"),
            });
            return next;
          }
          case "month": {
            const next =
              direction === "next" ? addMonths(prev, 1) : subMonths(prev, 1);
            log.debug("Navigate", {
              direction,
              viewMode,
              from: prevStr,
              to: format(next, "yyyy-MM-dd"),
            });
            return next;
          }
          default:
            log.trace("Navigate:noop", { direction, viewMode, from: prevStr });
            return prev;
        }
      });
    },
    [viewMode],
  );

  const handleToday = useCallback(() => {
    setCurrentDate((prev) => {
      const next = new Date();
      log.debug("Navigate:today", {
        from: format(prev, "yyyy-MM-dd"),
        to: format(next, "yyyy-MM-dd"),
      });
      return next;
    });
  }, []);

  const handleSetViewMode = useCallback((nextMode: ViewMode) => {
    setViewMode((prev) => {
      if (prev !== nextMode) {
        log.info("View mode:change", { from: prev, to: nextMode });
      } else {
        log.trace("View mode:unchanged", { mode: nextMode });
      }
      return nextMode;
    });
  }, []);

  // ✅ Status Color Helper
  const getStatusColor = (
    status: string,
  ):
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning" => {
    switch (status) {
      case "scheduled":
        return "primary";
      case "confirmed":
        return "success";
      case "completed":
        return "info";
      case "canceled":
        return "error";
      default:
        return "default";
    }
  };

  const getPriorityColor = (
    priority: string,
  ):
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning" => {
    switch (priority) {
      case "urgent":
        return "error";
      case "high":
        return "warning";
      case "medium":
        return "primary";
      case "low":
        return "default";
      default:
        return "default";
    }
  };

  const getAppointmentDoctorName = useCallback(
    (appointment: Appointment): string => {
      const a: any = appointment;
      const explicit = String(a?.DoctorName ?? a?.doctorName ?? "").trim();
      if (explicit) return explicit;

      const doctorId = a?.DoctorId ?? a?.doctorId;
      if (doctorId === undefined || doctorId === null) return "";

      return doctors.find((d) => d.id === String(doctorId))?.name?.trim() || "";
    },
    [doctors],
  );

  const getAppointmentDoctorDateLine = useCallback(
    (appointment: Appointment): string => {
      const a: any = appointment;
      const doctorName = getAppointmentDoctorName(appointment);
      const date = String(
        a?.date ?? a?.Date ?? a?.data ?? a?.Data ?? "",
      ).trim();
      return [doctorName, date].filter(Boolean).join(" • ");
    },
    [getAppointmentDoctorName],
  );

  const getAppointmentMetaLine = useCallback(
    (appointment: Appointment): string => {
      const a: any = appointment;
      const doctorName = getAppointmentDoctorName(appointment);
      const date = String(
        a?.date ?? a?.Date ?? a?.data ?? a?.Data ?? "",
      ).trim();
      const time = String(a?.time ?? a?.Time ?? a?.ora ?? a?.Ora ?? "").trim();
      return [doctorName, date, time].filter(Boolean).join(" • ");
    },
    [getAppointmentDoctorName],
  );

  // ✅ Filter Helpers
  const filteredAppointments = useMemo(() => {
    let filtered = [...appointments];

    // Apply date range filter based on view
    const startDate =
      viewMode === "month"
        ? startOfMonth(currentDate)
        : viewMode === "week"
          ? startOfWeek(currentDate, { weekStartsOn: 1 })
          : currentDate;

    const endDate =
      viewMode === "month"
        ? endOfMonth(currentDate)
        : viewMode === "week"
          ? endOfWeek(currentDate, { weekStartsOn: 1 })
          : currentDate;

    if (viewMode !== "list") {
      filtered = filtered.filter((appointment) => {
        const appointmentDate = parseISO(appointment.date);
        return appointmentDate >= startDate && appointmentDate <= endDate;
      });
    }

    return filtered;
  }, [appointments, currentDate, viewMode]);

  // ✅ Effects
  useEffect(() => {
    log.debug("Effect:initial load (appointments/doctors)");
    loadAppointments();
    loadDoctors();
  }, [loadAppointments, loadDoctors]);

  // Debounced patient search: only run when dialog is open and user types.
  useEffect(() => {
    if (!dialogOpen) return;
    const q = patientQuery.trim();
    if (q.length < 2) return;

    const handle = window.setTimeout(() => {
      loadPatients(q, 25);
    }, 300);

    return () => window.clearTimeout(handle);
  }, [dialogOpen, patientQuery, loadPatients]);

  useEffect(() => {
    log.trace("Effect:date/view changed -> reload appointments", {
      viewMode,
      currentDate: format(currentDate, "yyyy-MM-dd"),
    });
    loadAppointments();
  }, [currentDate, viewMode, loadAppointments]);

  // ✅ Render Calendar Grid for Week/Month View
  const renderCalendarGrid = () => {
    if (viewMode === "list") {
      return (
        <Grid container spacing={2}>
          {filteredAppointments.map((appointment) => (
            <Grid item xs={12} key={appointment.id}>
              <Card
                elevation={2}
                sx={{
                  borderLeft: `4px solid ${
                    getStatusColor(appointment.status) === "default"
                      ? theme.palette.grey[400]
                      : theme.palette[
                          getStatusColor(appointment.status) as
                            | "primary"
                            | "secondary"
                            | "error"
                            | "info"
                            | "success"
                            | "warning"
                        ]?.main || theme.palette.primary.main
                  }`, // ✅ Fixed border color
                  "&:hover": { elevation: 4 },
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 1,
                    }}
                  >
                    <Typography variant="h6" component="div">
                      {appointment.PatientName}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Chip
                        label={appointment.status}
                        color={getStatusColor(appointment.status)}
                        size="small"
                      />
                      <Chip
                        label={appointment.priority}
                        color={getPriorityColor(
                          appointment.priority || "medium",
                        )}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </Box>

                  <Typography color="text.secondary" gutterBottom>
                    {getAppointmentMetaLine(appointment) || "—"}
                  </Typography>

                  <Typography variant="body2">{appointment.reason}</Typography>

                  {appointment.notes && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1, fontStyle: "italic" }}
                    >
                      📝 {appointment.notes}
                    </Typography>
                  )}
                </CardContent>

                <CardActions>
                  <Button
                    size="small"
                    onClick={() => handleView(appointment)}
                    startIcon={<ViewIcon />}
                  >
                    Vezi
                  </Button>
                  <Button
                    size="small"
                    onClick={() => handleEdit(appointment)}
                    startIcon={<EditIcon />}
                  >
                    Editează
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleDeleteAppointment(appointment)}
                    startIcon={<DeleteIcon />}
                  >
                    Șterge
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      );
    }

    // Week/Month calendar grid implementation
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          {format(currentDate, "MMMM yyyy", { locale: ro })}
        </Typography>
        <Grid container spacing={1}>
          {filteredAppointments.map((appointment) => (
            <Grid item xs={12} sm={6} md={4} key={appointment.id}>
              <Card
                elevation={1}
                sx={{
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  "&:hover": { elevation: 3 },
                }}
                onClick={() => handleView(appointment)}
              >
                <CardContent sx={{ p: 2, flexGrow: 1 }}>
                  <Typography variant="subtitle2" noWrap>
                    {appointment.time} - {appointment.PatientName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {appointment.reason}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {getAppointmentDoctorDateLine(appointment) || "—"}
                  </Typography>
                  <Chip
                    label={appointment.status}
                    color={getStatusColor(appointment.status)}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </CardContent>

                <CardActions sx={{ px: 1, pb: 1, pt: 0 }}>
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleView(appointment);
                    }}
                    startIcon={<ViewIcon />}
                  >
                    Vezi
                  </Button>
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(appointment);
                    }}
                    startIcon={<EditIcon />}
                  >
                    Editează
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteAppointment(appointment);
                    }}
                    startIcon={<DeleteIcon />}
                  >
                    Șterge
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ro">
      <Box sx={{ width: "100%", height }}>
        {/* Header */}
        <Box
          sx={{
            mb: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {title}
          </Typography>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={{
              borderRadius: 3,
              textTransform: "none",
              fontWeight: 600,
              px: 3,
            }}
          >
            Programare nouă
          </Button>
        </Box>

        {/* Toolbar */}
        <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            {/* Navigation */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton onClick={() => handleNavigate("prev")} size="small">
                <PrevIcon />
              </IconButton>

              <Button
                variant="outlined"
                onClick={handleToday}
                size="small"
                startIcon={<TodayIcon />}
              >
                Astăzi
              </Button>

              <IconButton onClick={() => handleNavigate("next")} size="small">
                <NextIcon />
              </IconButton>

              <Typography variant="h6" sx={{ ml: 2, minWidth: 200 }}>
                {viewMode === "month" &&
                  format(currentDate, "MMMM yyyy", { locale: ro })}
                {viewMode === "week" &&
                  `${format(
                    startOfWeek(currentDate, { weekStartsOn: 1 }),
                    "dd MMM",
                    { locale: ro },
                  )} - ${format(
                    endOfWeek(currentDate, { weekStartsOn: 1 }),
                    "dd MMM yyyy",
                    { locale: ro },
                  )}`}
                {viewMode === "day" &&
                  format(currentDate, "dd MMMM yyyy", { locale: ro })}
                {viewMode === "list" && "Toate programările"}
              </Typography>
            </Box>

            {/* View Mode Buttons */}
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant={viewMode === "day" ? "contained" : "outlined"}
                size="small"
                onClick={() => handleSetViewMode("day")}
                startIcon={<CalendarToday />}
              >
                Zi
              </Button>
              <Button
                variant={viewMode === "week" ? "contained" : "outlined"}
                size="small"
                onClick={() => handleSetViewMode("week")}
                startIcon={<CalendarViewWeek />}
              >
                Săptămână
              </Button>
              <Button
                variant={viewMode === "month" ? "contained" : "outlined"}
                size="small"
                onClick={() => handleSetViewMode("month")}
                startIcon={<CalendarViewMonth />}
              >
                Lună
              </Button>
              <Button
                variant={viewMode === "list" ? "contained" : "outlined"}
                size="small"
                onClick={() => handleSetViewMode("list")}
                startIcon={<EventIcon />}
              >
                Listă
              </Button>

              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

              <IconButton
                onClick={() => {
                  log.info("Manual refresh");
                  loadAppointments();
                }}
                disabled={loading}
                size="small"
              >
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>
        </Paper>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading */}
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Calendar Content */}
        <Paper
          elevation={2}
          sx={{ borderRadius: 3, overflow: "hidden", minHeight: 500 }}
        >
          {renderCalendarGrid()}
        </Paper>

        {/* Appointment Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={handleDialogClose}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle
            sx={{
              borderBottom: "1px solid",
              borderColor: "divider",
              fontWeight: 600,
            }}
          >
            {dialogMode === "view" && "Detalii Programare"}
            {dialogMode === "edit" && "Editare Programare"}
            {dialogMode === "add" && "Programare Nouă"}
          </DialogTitle>

          <DialogContent sx={{ pt: 3 }}>
            <Stack spacing={2}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Stack spacing={1} sx={{ flex: 1 }}>
                  <Autocomplete
                    fullWidth
                    disabled={dialogMode === "view"}
                    options={patients}
                    loading={patientsLoading}
                    value={
                      formData.PatientId
                        ? (patients.find(
                            (p) => p.id === formData.PatientId,
                          ) ?? {
                            id: formData.PatientId,
                            name:
                              (formData as any).PatientName ||
                              (formData as any).patientName ||
                              (formData as any).NumePrenume ||
                              (formData as any).numePrenume ||
                              String(formData.PatientId),
                          })
                        : null
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    getOptionLabel={(option) => option.name}
                    onOpen={() => {
                      // Preload first page when the user opens the picker.
                      if (patients.length === 0 && !patientsLoading) {
                        loadPatients("", 25);
                      }
                    }}
                    onChange={(_, selected) => {
                      const selectedId = selected?.id;
                      handleFormFieldChange("PatientId", selectedId);
                      setFormData((prev) => ({
                        ...prev,
                        PatientName: selected?.name,
                      }));
                    }}
                    onInputChange={(_, newInputValue, reason) => {
                      // Only update query for real typing/clear; ignore reset events.
                      if (reason === "input") setPatientQuery(newInputValue);
                      if (reason === "clear") setPatientQuery("");
                    }}
                    noOptionsText={
                      patientQuery.trim().length < 2
                        ? "Introduceți cel puțin 2 caractere"
                        : "Niciun pacient găsit"
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Pacient *"
                        error={!!formErrors.PatientId}
                        helperText={formErrors.PatientId}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {patientsLoading ? (
                                <CircularProgress color="inherit" size={18} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />

                  {dialogMode !== "view" && (
                    <Button
                      variant="text"
                      onClick={() => {
                        setNewPatientErrors({});
                        setNewPatientDialogOpen(true);
                      }}
                      startIcon={<AddIcon />}
                      sx={{ alignSelf: "flex-start" }}
                    >
                      Adaugă pacient nou
                    </Button>
                  )}
                </Stack>

                <TextField
                  select
                  label="Doctor *"
                  value={formData.DoctorId || ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    handleFormFieldChange("DoctorId", e.target.value);
                    const selectedDoctor = doctors.find(
                      (d) => d.id === e.target.value,
                    );
                    if (selectedDoctor) {
                      setFormData((prev) => ({
                        ...prev,
                        DoctorName: selectedDoctor.name,
                      }));
                    }
                  }}
                  disabled={dialogMode === "view"}
                  fullWidth
                  error={!!formErrors.DoctorId}
                  helperText={formErrors.DoctorId}
                  sx={{ flex: 1 }}
                >
                  {doctors.map((doctor) => (
                    <MenuItem key={doctor.id} value={doctor.id}>
                      {doctor.name} - {doctor.specialization}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box sx={{ display: "flex", gap: 2 }}>
                {/* ✅ Fixed DatePicker */}
                <DatePicker
                  label="Data *"
                  value={stringToDayjs(formData.date || "")} // ✅ Convert string to Dayjs
                  onChange={(date) => handleFormFieldChange("date", date)} // ✅ Pass Dayjs directly
                  disabled={dialogMode === "view"}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!formErrors.date,
                      helperText: formErrors.date,
                    },
                  }}
                />

                {/* ✅ Fixed TimePicker */}
                <TimePicker
                  label="Ora *"
                  value={stringToTime(formData.time || "")} // ✅ Convert time string to Dayjs
                  onChange={(time) => handleFormFieldChange("time", time)} // ✅ Pass Dayjs directly
                  disabled={dialogMode === "view"}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!formErrors.time,
                      helperText: formErrors.time,
                    },
                  }}
                />
              </Box>

              <TextField
                label="Motivul consultației *"
                value={formData.reason || ""}
                onChange={(e) =>
                  handleFormFieldChange("reason", e.target.value)
                }
                disabled={dialogMode === "view"}
                fullWidth
                multiline
                rows={2}
                error={!!formErrors.reason}
                helperText={formErrors.reason}
              />

              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  select
                  label="Status"
                  value={formData.status || "scheduled"}
                  onChange={(e) =>
                    handleFormFieldChange("status", e.target.value)
                  }
                  disabled={dialogMode === "view"}
                  fullWidth
                >
                  <MenuItem value="scheduled">Programat</MenuItem>
                  <MenuItem value="confirmed">Confirmat</MenuItem>
                  <MenuItem value="completed">Finalizat</MenuItem>
                  <MenuItem value="canceled">Anulat</MenuItem>
                </TextField>

                <TextField
                  select
                  label="Prioritate"
                  value={formData.priority || "medium"}
                  onChange={(e) =>
                    handleFormFieldChange("priority", e.target.value)
                  }
                  disabled={dialogMode === "view"}
                  fullWidth
                >
                  <MenuItem value="low">Scăzută</MenuItem>
                  <MenuItem value="medium">Medie</MenuItem>
                  <MenuItem value="high">Ridicată</MenuItem>
                  <MenuItem value="urgent">Urgentă</MenuItem>
                </TextField>

                <TextField
                  label="Durata (min)"
                  type="number"
                  value={formData.duration || 30}
                  onChange={(e) =>
                    handleFormFieldChange("duration", Number(e.target.value))
                  }
                  disabled={dialogMode === "view"}
                  fullWidth
                  inputProps={{ min: 15, max: 180, step: 15 }}
                />
              </Box>

              <TextField
                label="Observații"
                value={formData.notes || ""}
                onChange={(e) => handleFormFieldChange("notes", e.target.value)}
                disabled={dialogMode === "view"}
                fullWidth
                multiline
                rows={3}
                placeholder="Observații suplimentare..."
              />
            </Stack>
          </DialogContent>

          <DialogActions
            sx={{ p: 3, borderTop: "1px solid", borderColor: "divider" }}
          >
            <Button
              onClick={handleDialogClose}
              variant="outlined"
              disabled={loading}
            >
              {dialogMode === "view" ? "Închide" : "Anulează"}
            </Button>

            {dialogMode !== "view" && (
              <Button
                variant="contained"
                onClick={() => handleSaveAppointment(dialogMode === "edit")}
                disabled={loading}
                sx={{ minWidth: 140, fontWeight: 600 }}
              >
                {loading
                  ? "Se salvează..."
                  : dialogMode === "edit"
                    ? "Salvează Modificările"
                    : "Creează Programarea"}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* New Patient Dialog */}
        <Dialog
          open={newPatientDialogOpen}
          onClose={() => {
            if (newPatientSaving) return;
            setNewPatientDialogOpen(false);
            setNewPatientErrors({});
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>Pacient Nou</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {newPatientErrors._global && (
                <Alert severity="error">{newPatientErrors._global}</Alert>
              )}

              <TextField
                label="Nume și prenume *"
                value={newPatientData.NumePrenume}
                onChange={(e) =>
                  setNewPatientData((p) => ({
                    ...p,
                    NumePrenume: e.target.value,
                  }))
                }
                fullWidth
                error={!!newPatientErrors.NumePrenume}
                helperText={newPatientErrors.NumePrenume}
                disabled={newPatientSaving}
              />

              <TextField
                label="CNP *"
                value={newPatientData.CNP}
                onChange={(e) =>
                  setNewPatientData((p) => ({ ...p, CNP: e.target.value }))
                }
                fullWidth
                error={!!newPatientErrors.CNP}
                helperText={newPatientErrors.CNP}
                disabled={newPatientSaving}
                inputProps={{ inputMode: "numeric" }}
              />

              <TextField
                label="Email *"
                value={newPatientData.Email}
                onChange={(e) =>
                  setNewPatientData((p) => ({ ...p, Email: e.target.value }))
                }
                fullWidth
                error={!!newPatientErrors.Email}
                helperText={newPatientErrors.Email}
                disabled={newPatientSaving}
              />

              <TextField
                label="Telefon mobil *"
                value={newPatientData.TelefonMobil}
                onChange={(e) =>
                  setNewPatientData((p) => ({
                    ...p,
                    TelefonMobil: e.target.value,
                  }))
                }
                fullWidth
                error={!!newPatientErrors.TelefonMobil}
                helperText={newPatientErrors.TelefonMobil}
                disabled={newPatientSaving}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => {
                if (newPatientSaving) return;
                setNewPatientDialogOpen(false);
                setNewPatientErrors({});
              }}
              disabled={newPatientSaving}
            >
              Anulează
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateNewPatient}
              disabled={newPatientSaving}
            >
              {newPatientSaving ? "Se salvează..." : "Creează"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Floating Action Button */}
        <Fab
          color="primary"
          sx={{ position: "fixed", bottom: 24, right: 24 }}
          onClick={handleAdd}
        >
          <AddIcon />
        </Fab>
      </Box>
    </LocalizationProvider>
  );
};

export default AppointmentSchedule;
